import { createFileRoute } from '@tanstack/react-router';
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Card,
  Center,
  Divider,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Switch,
  TagsInput,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconEdit,
  IconMessageBolt,
  IconPlus,
  IconTrash,
} from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { trpc } from '../../lib/trpc';

type TriggerType = 'comment' | 'story_mention' | 'story_reply';
type MatchMode = 'contains' | 'exact' | 'any';
type Scope = 'all' | 'specific';

interface RuleForm {
  name: string;
  triggerType: TriggerType;
  enabled: boolean;
  scope: Scope;
  mediaIds: string[];
  matchMode: MatchMode;
  keywords: string[];
  publicReplyText: string;
  dmText: string;
  captureAsLead: boolean;
  applyLabelId: number | null;
}

const EMPTY_FORM: RuleForm = {
  name: '',
  triggerType: 'comment',
  enabled: true,
  scope: 'all',
  mediaIds: [],
  matchMode: 'contains',
  keywords: [],
  publicReplyText: '',
  dmText: '',
  captureAsLead: true,
  applyLabelId: null,
};

const TRIGGER_LABEL: Record<TriggerType, string> = {
  comment: 'Comment',
  story_mention: 'Story mention',
  story_reply: 'Story reply',
};

function InstagramAutomations() {
  const accounts = trpc.instagram.accounts.list.useQuery();
  const [accountId, setAccountId] = useState<number | null>(null);

  useEffect(() => {
    if (accountId == null && accounts.data && accounts.data.length > 0) {
      setAccountId(accounts.data[0]!.id);
    }
  }, [accounts.data, accountId]);

  const accountOptions = (accounts.data ?? []).map((a) => ({
    value: String(a.id),
    label: a.name ? `${a.name} (@${a.username})` : `@${a.username}`,
  }));

  return (
    <Stack p="md" gap="md">
      <Group justify="space-between">
        <Title order={3}>Comment & story automations</Title>
        <Select
          w={280}
          placeholder="Select account"
          data={accountOptions}
          value={accountId != null ? String(accountId) : null}
          onChange={(v) => setAccountId(v ? Number(v) : null)}
          allowDeselect={false}
          comboboxProps={{ withinPortal: true }}
        />
      </Group>

      <Alert
        icon={<IconAlertTriangle size={16} />}
        color="yellow"
        variant="light"
        title="Requires Meta App Review"
      >
        Comment-to-DM automation needs the <b>instagram_manage_comments</b> permission approved in
        Meta App Review before comment webhooks are delivered. You can build rules now; they
        activate once the app is approved.
      </Alert>

      {accounts.isPending ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : accountOptions.length === 0 ? (
        <Text c="dimmed">Connect an Instagram account first.</Text>
      ) : accountId == null ? null : (
        <RuleList accountId={accountId} />
      )}
    </Stack>
  );
}

function RuleList({ accountId }: { accountId: number }) {
  const utils = trpc.useUtils();
  const list = trpc.instagram.automations.list.useQuery({});
  const labels = trpc.contacts.labels.list.useQuery();
  const toggle = trpc.instagram.automations.toggle.useMutation({
    onSuccess: () => void utils.instagram.automations.list.invalidate(),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const del = trpc.instagram.automations.delete.useMutation({
    onSuccess: () => void utils.instagram.automations.list.invalidate(),
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const [editing, setEditing] = useState<{ uid: string | null; form: RuleForm } | null>(null);

  const rows = (list.data ?? []).filter((r) => r.instagramAccountId === accountId);
  const labelName = (id: number | null) =>
    id == null ? null : (labels.data?.find((l) => l.id === id)?.title ?? `#${id}`);

  return (
    <Stack gap="md" maw={820}>
      <Group>
        <Button
          leftSection={<IconPlus size={16} />}
          onClick={() => setEditing({ uid: null, form: EMPTY_FORM })}
        >
          New automation
        </Button>
      </Group>

      {list.isPending ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : rows.length === 0 ? (
        <Card withBorder>
          <Text c="dimmed">
            No automations yet. Create one to auto-reply to comments and DM the commenter.
          </Text>
        </Card>
      ) : (
        <Stack gap="sm">
          {rows.map((r) => (
            <Card key={r.uid} withBorder padding="sm">
              <Group justify="space-between" wrap="nowrap" align="flex-start">
                <Stack gap={4} style={{ minWidth: 0 }}>
                  <Group gap="xs" wrap="nowrap">
                    <IconMessageBolt size={16} />
                    <Text fw={600} truncate>
                      {r.name}
                    </Text>
                    <Badge size="xs" variant="light" color="grape">
                      {TRIGGER_LABEL[r.triggerType]}
                    </Badge>
                    <Badge
                      size="xs"
                      variant="light"
                      color={r.matchMode === 'any' ? 'gray' : 'blue'}
                    >
                      {r.matchMode === 'any'
                        ? 'any comment'
                        : `${r.matchMode}: ${r.keywords.join(', ') || '—'}`}
                    </Badge>
                  </Group>
                  <Group gap={6} wrap="wrap">
                    {r.publicReplyText && (
                      <Badge size="xs" variant="outline" color="teal">
                        public reply
                      </Badge>
                    )}
                    {r.dmText && (
                      <Badge size="xs" variant="outline" color="pink">
                        sends DM
                      </Badge>
                    )}
                    {r.captureAsLead && (
                      <Badge size="xs" variant="outline" color="indigo">
                        capture lead
                        {labelName(r.applyLabelId) ? ` · ${labelName(r.applyLabelId)}` : ''}
                      </Badge>
                    )}
                    {r.scope === 'specific' && (
                      <Badge size="xs" variant="outline" color="orange">
                        {r.mediaIds.length} post(s)
                      </Badge>
                    )}
                  </Group>
                </Stack>
                <Group gap="xs" wrap="nowrap">
                  <Switch
                    size="sm"
                    checked={r.enabled}
                    onChange={(e) =>
                      toggle.mutate({ uid: r.uid, enabled: e.currentTarget.checked })
                    }
                  />
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    aria-label="Edit"
                    onClick={() =>
                      setEditing({
                        uid: r.uid,
                        form: {
                          name: r.name,
                          triggerType: r.triggerType,
                          enabled: r.enabled,
                          scope: r.scope,
                          mediaIds: r.mediaIds,
                          matchMode: r.matchMode,
                          keywords: r.keywords,
                          publicReplyText: r.publicReplyText ?? '',
                          dmText: r.dmText ?? '',
                          captureAsLead: r.captureAsLead,
                          applyLabelId: r.applyLabelId,
                        },
                      })
                    }
                  >
                    <IconEdit size={16} />
                  </ActionIcon>
                  <ActionIcon
                    variant="subtle"
                    color="red"
                    aria-label="Delete"
                    loading={del.isPending && del.variables?.uid === r.uid}
                    onClick={() => del.mutate({ uid: r.uid })}
                  >
                    <IconTrash size={16} />
                  </ActionIcon>
                </Group>
              </Group>
            </Card>
          ))}
        </Stack>
      )}

      {editing && (
        <RuleEditor
          accountId={accountId}
          uid={editing.uid}
          initial={editing.form}
          labelOptions={(labels.data ?? []).map((l) => ({ value: String(l.id), label: l.title }))}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void utils.instagram.automations.list.invalidate();
          }}
        />
      )}
    </Stack>
  );
}

function RuleEditor({
  accountId,
  uid,
  initial,
  labelOptions,
  onClose,
  onSaved,
}: {
  accountId: number;
  uid: string | null;
  initial: RuleForm;
  labelOptions: { value: string; label: string }[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = useState<RuleForm>(initial);
  const create = trpc.instagram.automations.create.useMutation({
    onSuccess: onSaved,
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });
  const update = trpc.instagram.automations.update.useMutation({
    onSuccess: onSaved,
    onError: (e) => notifications.show({ message: e.message, color: 'red' }),
  });

  const set = <K extends keyof RuleForm>(k: K, v: RuleForm[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const valid =
    form.name.trim().length > 0 &&
    (form.matchMode === 'any' || form.keywords.length > 0) &&
    (form.scope === 'all' || form.mediaIds.length > 0) &&
    (!!form.publicReplyText.trim() || !!form.dmText.trim() || form.captureAsLead);

  function save() {
    const common = {
      name: form.name.trim(),
      triggerType: form.triggerType,
      enabled: form.enabled,
      scope: form.scope,
      mediaIds: form.mediaIds,
      matchMode: form.matchMode,
      keywords: form.keywords,
      publicReplyText: form.publicReplyText.trim() || null,
      dmText: form.dmText.trim() || null,
      captureAsLead: form.captureAsLead,
      applyLabelId: form.applyLabelId,
    };
    if (uid) update.mutate({ uid, ...common });
    else create.mutate({ instagramAccountId: accountId, ...common });
  }

  return (
    <Modal
      opened
      onClose={onClose}
      title={uid ? 'Edit automation' : 'New automation'}
      size="lg"
      centered
    >
      <Stack gap="sm">
        <TextInput
          label="Name"
          placeholder="Price requests → DM catalog"
          value={form.name}
          onChange={(e) => set('name', e.currentTarget.value)}
        />
        <Group grow>
          <Select
            label="Trigger"
            data={[
              { value: 'comment', label: 'Comment on post/reel' },
              { value: 'story_mention', label: 'Story mention' },
              { value: 'story_reply', label: 'Story reply' },
            ]}
            value={form.triggerType}
            allowDeselect={false}
            onChange={(v) => set('triggerType', (v as TriggerType) ?? 'comment')}
          />
          <Select
            label="Match"
            data={[
              { value: 'contains', label: 'Contains keyword' },
              { value: 'exact', label: 'Exact match' },
              { value: 'any', label: 'Any (no keyword)' },
            ]}
            value={form.matchMode}
            allowDeselect={false}
            onChange={(v) => set('matchMode', (v as MatchMode) ?? 'contains')}
          />
        </Group>
        {form.matchMode !== 'any' && (
          <TagsInput
            label="Keywords"
            placeholder="Type and press Enter"
            value={form.keywords}
            onChange={(v) => set('keywords', v)}
          />
        )}
        <Group grow>
          <Select
            label="Applies to"
            data={[
              { value: 'all', label: 'All posts/reels' },
              { value: 'specific', label: 'Specific media' },
            ]}
            value={form.scope}
            allowDeselect={false}
            onChange={(v) => set('scope', (v as Scope) ?? 'all')}
          />
          {form.scope === 'specific' && (
            <TagsInput
              label="Media IDs"
              placeholder="Paste media ids"
              value={form.mediaIds}
              onChange={(v) => set('mediaIds', v)}
            />
          )}
        </Group>
        <Divider label="Actions" labelPosition="left" />
        <Textarea
          label="Public reply (optional)"
          placeholder="Thanks for your comment! Check your DMs 💬"
          autosize
          minRows={2}
          value={form.publicReplyText}
          onChange={(e) => set('publicReplyText', e.currentTarget.value)}
        />
        <Textarea
          label="Private DM (optional)"
          placeholder="Here's the link you asked for 👉 …"
          autosize
          minRows={2}
          value={form.dmText}
          onChange={(e) => set('dmText', e.currentTarget.value)}
        />
        <Group justify="space-between" align="flex-end">
          <Switch
            label="Capture commenter as a CRM lead"
            checked={form.captureAsLead}
            onChange={(e) => set('captureAsLead', e.currentTarget.checked)}
          />
          <Select
            w={220}
            label="Apply label (optional)"
            placeholder="No label"
            data={labelOptions}
            value={form.applyLabelId != null ? String(form.applyLabelId) : null}
            clearable
            onChange={(v) => set('applyLabelId', v ? Number(v) : null)}
            disabled={!form.captureAsLead}
            comboboxProps={{ withinPortal: true }}
          />
        </Group>
        <Group justify="flex-end" mt="xs">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button
            color="pink"
            loading={create.isPending || update.isPending}
            disabled={!valid}
            onClick={save}
          >
            {uid ? 'Save changes' : 'Create automation'}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}

export const Route = createFileRoute('/vendor/instagram/automations')({
  component: InstagramAutomations,
});
